import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useShop } from "@/context/ShopContext";
import { api } from "@/lib/api";
import { inr, COMBO } from "@/lib/content";

const ComboSuggest = ({ cart, addToCart, onNavigate }) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { min_price: COMBO.item, max_price: COMBO.item, limit: 50 } }).then((r) => setList(r.data));
  }, []);
  const inCart = cart.filter((i) => i.price === COMBO.item).map((i) => i.slug);
  const anchor = list.find((p) => inCart.includes(p.slug));
  const shared = (p) => (anchor ? p.occasions.filter((o) => anchor.occasions.includes(o)).length : 0);
  const picks = list
    .filter((p) => !inCart.includes(p.slug) && p.sizes.some((s) => p.stock[s] > 0))
    .sort((a, b) => shared(b) - shared(a) || b.popularity - a.popularity)
    .slice(0, 3);
  if (!picks.length) return null;
  return (
    <div data-testid="combo-suggest" className="rounded-2xl border border-dashed border-[#B85D43] bg-[#F4E4DE] p-4">
      <p className="text-sm font-semibold text-[#701A24]">Complete your 2 for {inr(COMBO.pair)} combo</p>
      <p className="mt-0.5 text-xs text-[#57534E]">Add one more ₹{COMBO.item} style – you'll pay {inr(COMBO.pair)} for both.</p>
      <div className="mt-3 space-y-3">
        {picks.map((p) => {
          const size = p.stock.M > 0 ? "M" : p.sizes.find((s) => p.stock[s] > 0);
          return (
            <div key={p.slug} data-testid={`combo-suggest-${p.slug}`} className="flex items-center gap-3 rounded-xl bg-white p-2">
              <img src={p.images[0]} alt={p.alt} className="h-16 w-12 rounded-lg object-cover object-top" />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-xs font-medium leading-snug">{p.name}</p>
                <Link to={`/products/${p.slug}`} onClick={onNavigate} className="text-[11px] text-[#B85D43] underline-offset-2 hover:underline">Choose size</Link>
              </div>
              <button data-testid={`combo-suggest-add-${p.slug}`} onClick={() => addToCart(p, size, p.colors[0].name, 1, false)} className="flex shrink-0 items-center gap-1 rounded-full bg-[#701A24] px-3 py-2 text-[11px] font-semibold text-white hover:bg-[#56131B]">
                <Plus className="h-3 w-3" /> Add · {size}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, subtotal, count, addToCart } = useShop();
  const nav = useNavigate();
  const comboUnits = cart.filter((i) => i.price === COMBO.item).reduce((s, i) => s + i.qty, 0);
  const restUnits = count - comboUnits + (comboUnits % 2);
  const msg = comboUnits % 2 === 1
    ? `Add 1 more ₹${COMBO.item} style – get both for ${inr(COMBO.pair)}`
    : comboUnits >= 2 ? `2 for ${inr(COMBO.pair)} combo unlocked × ${comboUnits / 2}`
    : restUnits >= 2 ? "Buy 2 offer unlocked: ₹50 off at checkout" : "Add 1 more piece to save ₹50 · Free shipping";
  const bundle = comboUnits >= 2 || restUnits >= 2;

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col bg-[#FAF7F2] p-0 sm:max-w-md">
        <div className="border-b border-[#E7E2DB] px-6 py-5">
          <SheetTitle className="font-serif-display text-2xl text-[#701A24]">Your Bag ({count})</SheetTitle>
          <p className={`mt-2 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${bundle ? "bg-[#1B4332] text-white" : "bg-[#EAE0D5] text-[#57534E]"}`}>
            <Truck className="h-3.5 w-3.5" /> {msg}
          </p>
        </div>
        {cart.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag className="h-10 w-10 text-[#D99B8E]" />
            <p className="font-serif-display text-2xl">Your bag is empty</p>
            <Link to="/collections/best-sellers" onClick={() => setCartOpen(false)} className="btn-primary" data-testid="cart-empty-shop-button">Shop Best Sellers</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {cart.map((i) => (
                <div key={i.key} data-testid={`cart-item-${i.slug}`} className="flex gap-4">
                  <img src={i.image} alt={i.name} className="h-28 w-20 rounded-xl object-cover" />
                  <div className="flex flex-1 flex-col">
                    <p className="text-sm font-medium leading-snug">{i.name}</p>
                    <p className="mt-1 text-xs text-[#78716C]">Size {i.size} · {i.color}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center rounded-full border border-[#E7E2DB] bg-white">
                        <button data-testid={`cart-qty-minus-${i.slug}`} onClick={() => updateQty(i.key, i.qty - 1)} className="p-2" aria-label="Decrease">{i.qty === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}</button>
                        <span className="w-6 text-center text-sm">{i.qty}</span>
                        <button data-testid={`cart-qty-plus-${i.slug}`} onClick={() => updateQty(i.key, Math.min(i.qty + 1, 10))} className="p-2" aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <span className="text-sm font-semibold">{inr(i.price * i.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {comboUnits % 2 === 1 && <ComboSuggest cart={cart} addToCart={addToCart} onNavigate={() => setCartOpen(false)} />}
            </div>
            <div className="border-t border-[#E7E2DB] bg-white px-6 py-5">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span data-testid="cart-subtotal" className="font-semibold">{inr(subtotal)}</span></div>
              <p className="mt-1 text-xs text-[#78716C]">Combo price, coupons & UPI discount applied at checkout.</p>
              <button data-testid="cart-checkout-button" onClick={() => { setCartOpen(false); nav("/checkout"); }} className="btn-primary mt-4 w-full">Checkout Securely</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
